import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { RadioButtonModule } from 'primeng/radiobutton';
import { CheckboxModule } from 'primeng/checkbox';
import { SelectModule } from 'primeng/select';
import { ButtonModule } from 'primeng/button';
import { CreateExportJobRequest } from '../services/export/export-api.service';
import { ColumnConfig } from '../dynamic-listing/dynamic-listing';
import { openInNewTab } from '../utils/url.utils';

export interface ExportDialogData {
  /** Module name for the export (e.g., 'client', 'client_invoice') */
  module: string;
  /** Column configurations from the list component */
  columns: ColumnConfig[];
  /** Current filter values from the list component */
  filters: Record<string, any>;
  /** Base filters that should ALWAYS be applied, even if "Export All" is selected */
  baseFilters?: Record<string, any>;
  /** Default export format */
  defaultFormat?: 'excel' | 'csv';
}

@Component({
  selector: 'lib-export-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RadioButtonModule,
    CheckboxModule,
    SelectModule,
    ButtonModule
  ],
  templateUrl: './export-dialog.component.html',
  styleUrls: ['./export-dialog.component.css']
})
export class ExportDialogComponent implements OnInit {
  private dialogRef = inject(DynamicDialogRef);
  private dialogConfig = inject(DynamicDialogConfig);
  private router = inject(Router);

  // Data from parent
  module: string = '';
  columns: ColumnConfig[] = [];
  filters: Record<string, any> = {};
  baseFilters: Record<string, any> = {};

  // Form state
  dataScope: 'filtered' | 'all' = 'filtered';
  selectedColumns: string[] = [];
  selectAll: boolean = true;
  exportFormat: 'excel' | 'csv' = 'excel';

  formatOptions = [
    { label: 'Excel (.xlsx)', value: 'excel' },
    { label: 'CSV (.csv)', value: 'csv' }
  ];

  ngOnInit(): void {
    const data = this.dialogConfig.data as ExportDialogData;

    if (data) {
      this.module = data.module || '';
      this.columns = data.columns || [];
      this.filters = data.filters || {};
      this.baseFilters = data.baseFilters || {};
      this.exportFormat = data.defaultFormat || 'excel';

      // Initialize all columns as selected
      this.selectedColumns = this.columns.map(col => col.key);
      this.selectAll = true;
    }
  }

  onSelectAllChange(checked: boolean): void {
    this.selectAll = checked;
    if (checked) {
      this.selectedColumns = this.columns.map(col => col.key);
    } else {
      this.selectedColumns = [];
    }
  }

  onColumnToggle(): void {
    // Update selectAll based on current selection
    this.selectAll = this.selectedColumns.length === this.columns.length;
  }

  isColumnSelected(key: string): boolean {
    return this.selectedColumns.includes(key);
  }

  toggleColumn(key: string): void {
    const index = this.selectedColumns.indexOf(key);
    if (index > -1) {
      this.selectedColumns.splice(index, 1);
    } else {
      this.selectedColumns.push(key);
    }
    this.onColumnToggle();
  }

  onCancel(): void {
    this.dialogRef.close(null);
  }

  onStartExport(): void {
    if (this.selectedColumns.length === 0) {
      // At least one column must be selected
      return;
    }

    // Build the export request
    const request: CreateExportJobRequest = {
      module: this.module,
      export_type: this.exportFormat,
      selected_columns: this.selectedColumns
    };

    // Start with base filters that MUST always be applied
    const finalFilters: Record<string, any> = { ...this.baseFilters };

    // Include user filters only if "filtered" scope is selected
    if (this.dataScope === 'filtered' && this.filters) {
      // Clean empty values from filters
      Object.entries(this.filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          finalFilters[key] = value;
        }
      });
    }

    if (Object.keys(finalFilters).length > 0) {
      request.filters = finalFilters;
    }

    this.dialogRef.close(request);
  }

  hasActiveFilters(): boolean {
    if (!this.filters) return false;
    return Object.values(this.filters).some(v => v !== undefined && v !== null && v !== '');
  }

  goToManageExports(): void {
    this.dialogRef.close(null);
    openInNewTab('/exports');
  }
}
